import {
  Button,
  Flex,
  AccordionButton,
  Tag,
  TagLabel,
  Tooltip,
  useDisclosure,
  Card,
  CardBody,
  AccordionPanel,
  CardHeader,
  Box,
} from '@chakra-ui/react';
import React, { useCallback } from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';

import { InvoiceDetailSideDrawer } from './invoiceDetail/InvoiceDetailSideDrawer';
import { InvoiceStatusBadge } from './InvoiceStatusBadge';
import { getInvoiceComputedStatus, getInvoiceType } from './utils';
import { InvoiceSummaryFragmentGQLType } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { TagIcon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';
import {
  dollarNumberWithCommas,
  truncateUuid,
} from 'packages/common-frontend/utils';
import { InvoiceConsolidatedDisbursements } from 'packages/invoices/ui/consolidatedDisbursements/InvoiceConsolidatedDisbursements';

const InvoiceSnapshotTag = ({
  icon,
  label,
  tooltip,
}: {
  icon: string;
  label: string;
  tooltip?: string;
}) => {
  return (
    <Tooltip label={tooltip} placement="top">
      <Tag variant="outline-muted" colorScheme="gray">
        <TagIcon icon={icon} />
        <TagLabel>{label}</TagLabel>
      </Tag>
    </Tooltip>
  );
};

export const InvoiceSnapshot = ({
  invoice,
  loanId,
}: {
  invoice: InvoiceSummaryFragmentGQLType;
  loanId?: string;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const computedStatus = getInvoiceComputedStatus({
    status: invoice.status,
    dueDate: invoice.dueDate,
    targetInvoiceDate: invoice.targetInvoiceDate ?? undefined,
  });

  // the link button is nested in the accordion button, so we need to prevent the accordion from toggling when clicking link
  const onOpenSidedrawer = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      onOpen();
    },
    [onOpen],
  );

  const totalAmount = loanId
    ? invoice.invoiceComponentsWithInvoiceItems
        .filter(
          (item) =>
            item.invoiceComponent.finleyLoanId === loanId ||
            item.invoiceItems.some(
              (invItem) => invItem.capitalizedLoanId === loanId,
            ),
        )
        .reduce(
          (sum, item) =>
            commonUtils.jsRound({
              value: sum + item.invoiceComponent.totalAmount,
              precision: 2,
            }),
          0,
        )
    : invoice.totalAmount;

  return (
    <Card
      size="lg"
      variant="outline"
      w="100%"
      id="funding-request-snapshot-container"
    >
      <AccordionButton>
        <CardHeader
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            fontWeight: 500,
            gap: '1rem',
            justifyContent: 'space-between',
            w: '100%',
          }}
        >
          <Flex gap="1rem" align="center" w="50%">
            <Box w="25%" textAlign="left">
              <Typography variant="section-subtitle" trim>
                Invoice
              </Typography>
            </Box>
            <Button
              colorScheme="blue"
              onClick={onOpenSidedrawer}
              variant="link"
              w="20%"
            >
              <Box textAlign="left" w="100%">
                <Typography
                  colorScheme="blue"
                  fontWeight="medium"
                  variant="base-muted"
                >
                  {truncateUuid(invoice.id)}
                </Typography>
              </Box>
            </Button>
            <InvoiceStatusBadge computedStatus={computedStatus} />
          </Flex>

          <Flex alignItems="center" gap="1rem" justifyContent="flex-end">
            <InvoiceSnapshotTag
              icon="calendar-check"
              label={
                invoice.paymentDate
                  ? commonDate.fullDateStrToHumanDateString(invoice.paymentDate)
                  : commonDate.fullDateStrToHumanDateString(invoice.dueDate)
              }
              tooltip={invoice.paymentDate ? 'Payment Date' : 'Due Date'}
            />
            <InvoiceSnapshotTag
              icon="file-invoice-dollar"
              label={getInvoiceType(invoice)}
              tooltip="Invoice Type"
            />
            <InvoiceSnapshotTag
              icon="circle-dollar"
              label={dollarNumberWithCommas(totalAmount) ?? '-'}
              tooltip="Total Amount"
            />
          </Flex>
        </CardHeader>
      </AccordionButton>
      <AccordionPanel>
        <CardBody pt="1rem">
          <InvoiceConsolidatedDisbursements invoice={invoice} />
          <InvoiceDetailSideDrawer
            invoiceId={invoice.id}
            isOpen={isOpen}
            onClose={onClose}
          />
        </CardBody>
      </AccordionPanel>
    </Card>
  );
};
